import Api from "lib/api/httpRequestHelper";
import MaskAndValid from "utils/maskAndValid";

export const CANAL_PRATELEIRA_INFINITA = "Prateleira Infinita";

export const CANAIS_OMNI = [
  "Clique e Retire",
  "Entrega pela Loja",
  CANAL_PRATELEIRA_INFINITA,
];

export function isPrateleiraInfinita(canal) {
  if (!canal) return false;
  return String(canal).trim().toLowerCase() === CANAL_PRATELEIRA_INFINITA.toLowerCase();
}

export function formatNumber(value) {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "number") return value.toLocaleString("pt-BR");
  const str = String(value);
  const num = Number(str.replace(/\./g, "").replace(",", "."));
  if (Number.isNaN(num)) return str;
  return num.toLocaleString("pt-BR");
}

export function formatCurrency(value) {
  if (value === null || value === undefined || value === "") return "-";
  return `R$ ${MaskAndValid.maskPrice(value)}`;
}

export function formatStatus(status) {
  if (!status) return "-";
  const normalized = String(status).trim().toLowerCase();
  if (normalized === "pago" || normalized === "lq") return "Pago";
  if (normalized === "pendente" || normalized === "ab") return "Pendente";
  return status;
}

export function isStatusPago(status) {
  const normalized = String(status || "")
    .trim()
    .toLowerCase();
  return normalized === "pago" || normalized === "lq";
}

export function mapSummaryFromApi(result) {
  if (!result) return null;

  return {
    totalVendas: result.TotalVendas ?? result.totalVendas ?? 0,
    totalPago: result.TotalPago ?? result.totalPago ?? 0,
    totalDebitos: result.TotalDebitos ?? result.totalDebitos ?? 0,
    quantidadePedidos:
      result.QuantidadePedidos ?? result.quantidadePedidos ?? 0,
  };
}

export function resolveFormatoArquivo(documento, { preferXml = false } = {}) {
  if (!documento) return null;

  const formato = (
    documento.formatoArquivo ??
    documento.FormatoArquivo ??
    ""
  )
    .toString()
    .trim()
    .toLowerCase();

  if (formato === "pdf" || formato === "xml") {
    return formato;
  }

  const pdfNoS3 = Boolean(documento.pdfNoS3 ?? documento.PdfNoS3);
  const xmlNoS3 = Boolean(documento.xmlNoS3 ?? documento.XmlNoS3);

  if (xmlNoS3 && !pdfNoS3) return "xml";
  if (pdfNoS3) return "pdf";
  if (xmlNoS3) return "xml";
  if (preferXml) return "xml";

  return null;
}

function mapDocumentoItemFromApi(documento) {
  if (!documento) {
    return {
      disponivel: false,
      chaveNf: null,
      formatoArquivo: null,
      pdfNoS3: false,
      xmlNoS3: false,
      numeroTitulo: null,
    };
  }

  const mapped = {
    disponivel: Boolean(documento.Disponivel ?? documento.disponivel),
    chaveNf: documento.ChaveNf ?? documento.chaveNf ?? null,
    pdfNoS3: Boolean(documento.PdfNoS3 ?? documento.pdfNoS3),
    xmlNoS3: Boolean(documento.XmlNoS3 ?? documento.xmlNoS3),
    numeroTitulo: documento.NumeroTitulo ?? documento.numeroTitulo ?? null,
    formatoArquivo: resolveFormatoArquivo({
      formatoArquivo: documento.FormatoArquivo ?? documento.formatoArquivo,
      pdfNoS3: Boolean(documento.PdfNoS3 ?? documento.pdfNoS3),
      xmlNoS3: Boolean(documento.XmlNoS3 ?? documento.xmlNoS3),
    }),
  };

  return mapped;
}

export function mapDocumentosFromApi(documentos) {
  if (!documentos) return null;

  return {
    notaServico: mapDocumentoItemFromApi(
      documentos.NotaServico ?? documentos.notaServico,
    ),
    notaVendaProduto: mapDocumentoItemFromApi(
      documentos.NotaVendaProduto ?? documentos.notaVendaProduto,
    ),
    boletoComissao: mapDocumentoItemFromApi(
      documentos.BoletoComissao ?? documentos.boletoComissao,
    ),
    boletoVenda: mapDocumentoItemFromApi(
      documentos.BoletoVenda ?? documentos.boletoVenda,
    ),
  };
}

function triggerBase64Download(base64Content, fileName) {
  const url = `data:application/octet-stream;base64,${base64Content}`;
  const downloadLink = document.createElement("a");
  downloadLink.href = url;
  downloadLink.target = "_blank";
  downloadLink.download = fileName;
  downloadLink.click();
}

async function downloadNotaPorTipo(chaveNf, tipoArquivo) {
  const value = await Api.get(
    tipoArquivo,
    "invoice",
    `?chaveNf=${chaveNf}`,
    false,
  );
  triggerBase64Download(value.Result.FileBase64, `${chaveNf}.${tipoArquivo}`);
}

export async function downloadNotaDocumento({
  chaveNf,
  formatoArquivo,
  pdfNoS3,
  xmlNoS3,
  preferXml = false,
}) {
  if (!chaveNf) {
    throw new Error("Documento não disponível para download.");
  }

  const tipoResolvido = resolveFormatoArquivo(
    { formatoArquivo, pdfNoS3, xmlNoS3 },
    { preferXml },
  );

  if (tipoResolvido) {
    await downloadNotaPorTipo(chaveNf, tipoResolvido);
    return;
  }

  if (preferXml) {
    try {
      await downloadNotaPorTipo(chaveNf, "xml");
      return;
    } catch (xmlError) {
      await downloadNotaPorTipo(chaveNf, "pdf");
      return;
    }
  }

  try {
    await downloadNotaPorTipo(chaveNf, "pdf");
  } catch (pdfError) {
    await downloadNotaPorTipo(chaveNf, "xml");
  }
}

export function mapPedidoFromApi(item, index) {
  const detalhe = item.Detalhe ?? item.detalhe ?? {};

  return {
    id: item.Id ?? item.id ?? `pedido-${index}`,
    marca: item.Marca ?? item.marca ?? "-",
    canal: item.Canal ?? item.canal ?? "-",
    numeroPedido: item.NumeroPedido ?? item.numeroPedido ?? "-",
    dataPedido: item.DataPedido ?? item.dataPedido ?? "",
    valorTotalPedido: item.ValorTotalPedido ?? item.valorTotalPedido ?? 0,
    valorComissao: item.ValorComissao ?? item.valorComissao ?? 0,
    status: item.Status ?? item.status ?? "pendente",
    detalhe: {
      frete: detalhe.Frete ?? detalhe.frete ?? 0,
      percentualComissao:
        detalhe.PercentualComissao ?? detalhe.percentualComissao ?? 0,
      vendaSellIn: detalhe.VendaSellIn ?? detalhe.vendaSellIn ?? 0,
      vendaLoja: detalhe.VendaLoja ?? detalhe.vendaLoja ?? 0,
      vendaMarca: detalhe.VendaMarca ?? detalhe.vendaMarca ?? 0,
      documentos: mapDocumentosFromApi(
        detalhe.Documentos ?? detalhe.documentos,
      ),
    },
  };
}

export function mapPedidosFromApi(result) {
  const lista = result?.ListaPedidos ?? result?.listaPedidos ?? result ?? [];

  return {
    pedidos: (Array.isArray(lista) ? lista : []).map(mapPedidoFromApi),
    totalRegistros: result?.TotalRegistros ?? result?.totalRegistros ?? 0,
  };
}
